/**
 * Панель темы — провенанс поста на экране согласования. Решение Ильи 25.08
 * (`docs/PHASE-2.md` §2.1), обоснование — `docs/phase-2/06-security-ops.md` §3.
 *
 * ЗАЧЕМ ОНА ЕСТЬ. Всю фазу 1 `RenderModel.topic` был литеральным `null`: согласующий видел
 * текст и не видел ни темы, ни домена, ни даты первоисточника. Значит сценарий
 * «правдоподобная выдумка с открытой площадки» не ловился ничем — префильтр меряет
 * релевантность, а не истинность. Эта панель и есть то, что превращает FR-4.1 из проверки
 * формы в настоящее согласование.
 *
 * ГДЕ ОНА СТОИТ. Полосой во всю ширину НАД тремя колонками, а не внутри одной из них.
 * Две причины, обе про смысл, а не про вёрстку:
 *   1. Порядок чтения. Колонки идут «исходник → превью → решение» (ui-design §5.2); вопрос
 *      «откуда это взялось» предшествует всем трём, потому что при плохом ответе на него
 *      остальные три читать незачем.
 *   2. Тема принадлежит черновику целиком, а не одной его колонке. Внутри превью она была бы
 *      прямым нарушением §6 («внутри панели превью не используется ни один фирменный токен»)
 *      и читалась бы как часть того, что увидит подписчик, — а это наш служебный блок.
 *
 * ДОМЕН КРУПНЕЕ АДРЕСА. Человек принимает решение по домену («это vc.ru или anthropic.com?»),
 * а не по длинному пути. Поэтому домен — 17px 600 цветом `--ink`, а канонический адрес
 * под ним — 11.5px muted. Различает их размер и цвет, а не гарнитура: моноширинного
 * начертания в интерфейсе нет нигде (решение заказчика 26.08.2026).
 *
 * ПОДПИСИ СНЯТЫ, ВЕЛИЧИНЫ ОСТАЛИСЬ (правка 3.3 от 08.09.2026, жалоба Ильи 07.09: «количество
 * текста просто невероятное»). Раньше каждая величина панели приходила предложением: дата
 * печаталась как «Дата первоисточника: …», а неразобранная — фразой вдвое длиннее. Теперь
 * видно ЗНАЧЕНИЕ, а объяснение висит нативным `title` рядом с ним. Три следствия, каждое
 * намеренное:
 *   • дату НЕ убираем совсем: без неё не отличить свежую новость от прошлогодней статьи, и
 *     ровно за этим панель заведена (`docs/PHASE-2.md` §2.1);
 *   • значок предупреждения при подставленной дате ОСТАЁТСЯ видимым: он и есть единственный
 *     признак, по которому подставленную дату видно, не наводя мышь;
 *   • обоснование выбора формата приезжает сюда пропом из шапки черновика и висит `title` на
 *     чипе формата. Подпись стоит рядом с величиной, которую объясняет, а не абзацем в шапке.
 *     Чип рисуется по `format_hint` темы: без формата объяснять нечего, и обоснование в этом
 *     случае не показывается вовсе. Код подписан словом (`formatLabel`): «формат: Новость»,
 *     а не «формат: news».
 *
 * СТРОКИ ОКНА СВЕЖЕСТИ НЕТ ВОВСЕ (партия 19.09, docs/release-2026-09-19.md §6). До неё здесь
 * печаталось «Окно свежести закрывается …» — предупреждение о дедлайне, после которого
 * утверждение, перенос и публикатор отказывали по возрасту материала. Протухание снято со
 * всех путей: интересна ли новость, решает человек, а не счётчик часов. Строка обещала бы
 * отказ, которого больше нет, поэтому снята и у датированных тем (поле `freshness_deadline`
 * контракт по-прежнему несёт — мониторинг его присылает). Возраст материала согласующий
 * видит по дате первоисточника ниже — ровно ради этого дата и осталась.
 *
 * Панель НЕ рендерится вовсе при `topic === null` (фикстура) — за это отвечает вызывающий
 * `DraftPanel`, чтобы на экране не возникало пустой рамки с надписью «темы нет».
 */

import type { JSX } from 'react';
import type { RenderTopic, RenderTopicMaterial } from '../api/types.ts';
import { stampMsk } from '../components/format.ts';
import { formatLabel } from '../components/formatLabel.ts';
import { IconExternal, IconWarn } from '../components/icons.tsx';
import styles from './TopicPanel.module.css';

/**
 * Контакт с панелью описан здесь, а не в `routes/contracts.ts`: там живут границы ТРЁХ
 * колонок, которые читают состояние черновика и умеют его менять. У панели темы нет ни
 * коллбэков, ни собственных запросов — она получает уже разобранное значение и только
 * показывает его.
 */
export interface TopicPanelProps {
  readonly topic: RenderTopic;
  /**
   * Обоснование выбора формата из версии черновика (`RenderModel.format_rationale`). Приходит
   * пропом, а не из темы: формат выбирает писатель по версии, а тема несёт лишь подсказку.
   * Показывается `title`-подсказкой на чипе формата — см. шапку файла.
   */
  readonly formatRationale?: string | null;
}

const ROLE_LABEL: Record<RenderTopicMaterial['role'], string> = {
  primary: 'первоисточник',
  supporting: 'подтверждает',
};

/**
 * Дата у материала есть ВСЕГДА: бэкенд считает её как `min(разобранная ?? fetched_at,
 * fetched_at)` (05-seams §1.4). Поэтому единственный способ отличить разобранную дату от
 * подставленной — сказать это словами; молчание читалось бы как «дата настоящая». Слова
 * переехали в `title`, а видимым признаком подстановки остался значок предупреждения.
 */
function dateTitle(m: RenderTopicMaterial): string {
  return m.published_at_origin === 'source'
    ? 'Дата первоисточника'
    : 'Дата первоисточника не разобрана — показано время получения';
}

function MaterialRow({ material }: { readonly material: RenderTopicMaterial }): JSX.Element {
  const substituted = material.published_at_origin !== 'source';
  return (
    <li className={styles['material']} data-testid="topic-material">
      <span className="pill st-hold">{ROLE_LABEL[material.role]}</span>
      <div className={styles['materialBody']}>
        <span className={styles['domain']} data-testid="topic-domain">
          {material.domain}
        </span>
        <a
          className={styles['url']}
          href={material.url_canonical}
          target="_blank"
          rel="noreferrer noopener"
        >
          {material.url_canonical}
          <IconExternal />
        </a>
        {substituted ? (
          <span className="val val-warn" data-testid="topic-date" title={dateTitle(material)}>
            <IconWarn />
            {stampMsk(material.published_at)}
          </span>
        ) : (
          <span className="micro" data-testid="topic-date" title={dateTitle(material)}>
            {stampMsk(material.published_at)}
          </span>
        )}
      </div>
    </li>
  );
}

export function TopicPanel({ topic, formatRationale = null }: TopicPanelProps): JSX.Element {
  return (
    <section className={styles['panel']} aria-label="Тема и первоисточник">
      <div className={styles['head']}>
        <span className="eyebrow">Тема и первоисточник</span>
        {topic.format_hint === null ? null : (
          <span className="chip-plain" title={formatRationale ?? undefined}>
            формат: {formatLabel(topic.format_hint)}
          </span>
        )}
      </div>

      <h2 className="h2">{topic.title}</h2>
      {topic.angle === null ? null : <p className={styles['line']}>Угол: {topic.angle}</p>}
      {topic.rationale === null ? null : (
        <p className={styles['line']} data-testid="topic-rationale">
          Почему это тема: {topic.rationale}
        </p>
      )}

      {topic.materials.length === 0 ? (
        <div className="val val-warn">
          У темы не осталось ни одного материала — источник этого поста неизвестен. Проверить
          написанное не по чему.
        </div>
      ) : (
        <ul className={styles['materials']}>
          {topic.materials.map((m) => (
            <MaterialRow key={m.url_canonical} material={m} />
          ))}
        </ul>
      )}
    </section>
  );
}
